import React, { useEffect, useRef, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import {
  Box,
  Button,
  Box as Container,
  EmptyState,
  Icon,
  Input,
  Pagination,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { PageHeader } from 'components/header/PageHeader'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useContactsQuery } from 'hooks/queries/useContactsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { contactActionAtom, contactAtom } from './atoms/contactAtoms'
import { ContactsSheets } from './components/ContactsSheets'
import { ContactsList } from './ContactsList'
import { stripSpecialCharacters } from './utils/utils'

// Limit initial counting contact in pagination to 5000 to avoid performance issues on backend side
const CONTACTS_INITIAL_TOTAL_COUNT_LIMIT = 5000

export const Contacts = () => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext() || {}
  const [searchQuery, setSearchQuery] = useState('')
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const [contactAction, setContactAction] = useAtom(contactActionAtom)
  const setContact = useSetAtom(contactAtom)
  const hasBeenInitialLoaded = useRef(false)

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = useSearchablePagination(
    10,
    debouncedSearchTerm
  )

  useEffect(() => {
    if (page > 0) {
      hasBeenInitialLoaded.current = true
    }
  }, [page])

  // Limit total count only for initial load without search query and pagination
  const limitInitialTotalCount = !page && !searchQuery && !hasBeenInitialLoaded.current
  const totalCountLimit = limitInitialTotalCount ? CONTACTS_INITIAL_TOTAL_COUNT_LIMIT : undefined

  const {
    data: { content: contactList, pagination } = {},
    isPending,
    isError,
  } = useContactsQuery({
    size: rowsPerPage,
    page,
    searchQuery: debouncedSearchTerm,
    totalCountLimit,
  })

  const { paging, showPagination } = usePaginationPagingPersist(pagination)
  const noResults = debouncedSearchTerm && !contactList?.length
  const showEmptyState = !isPending && !isError && !contactList?.length && !debouncedSearchTerm
  const showErrorState = isError && !isPending
  const showNoSearchResults = noResults && !isPending
  const showHeaderButton = (canEdit && !!contactList?.length) || (noResults && canEdit)
  const showSearch = isPending || !!contactList?.length || noResults

  useEffect(() => {
    setContact(null)
  }, [])

  const triggerOpenSheet = () => {
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.addContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        setContact(null)
      },
      meta: {
        enableCreateInstrument: false,
      },
    })
    setIsContactSheetOpen(true)
  }

  return (
    <Box>
      <PageHeader
        description={t('contacts.descriptionV2')}
        renderRightSection={
          showHeaderButton && (
            <Button
              testId="add-contact"
              disabled={viewOnly}
              onClick={triggerOpenSheet}
              iconElement={<Icon name="Plus" />}
            >
              {t('contacts.addContact')}
            </Button>
          )
        }
      />
      {/* Show error state */}
      {showErrorState && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('defaultError')}
          description={
            <>
              {`${t('requestErrors.comeBackLater')} `}
              <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                {t('requestErrors.refreshPage')}
              </Button>
            </>
          }
        />
      )}

      {/* Show content state */}
      {!isError && (
        <>
          {showSearch && (
            <Box paddingB={24} width="full">
              <Input
                placeholderText={t('contacts.searchPlaceholder')}
                onChange={event => setSearchQuery(stripSpecialCharacters(event.target.value))}
                value={searchQuery || ''}
                searchable={true}
                IconButton={
                  searchQuery && (
                    <Button
                      variant="ghost"
                      onClick={() => setSearchQuery('')}
                      iconElement={<Icon name="X" />}
                    />
                  )
                }
                iconPosition="right"
                testId="contact-search-field"
              />
            </Box>
          )}

          {isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

          {showNoSearchResults && (
            <NoSearchResults
              clearSearchCriteria={() => {
                setSearchQuery('')
              }}
            />
          )}

          {!!contactList?.length && !isPending && (
            <Box className="w-full">
              <ContactsList contacts={contactList} setIsSheetOpen={setIsContactSheetOpen} />
            </Box>
          )}

          {!!paging && !showPagination && (
            <Container className={`${isPending ? 'opacity-25' : ''}`}>
              <Pagination
                testId="contacts-pagination"
                page={page}
                rowsPerPage={rowsPerPage}
                total={paging?.total ?? 0}
                totalMaxCount={totalCountLimit}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Container>
          )}
        </>
      )}
      {/* Show empty state */}
      {showEmptyState && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('contacts.noContactHeadline')}
          description={t('contacts.addContactDescription')}
          PrimaryButton={
            canEdit && (
              <Button
                data-testId="add-contact"
                disabled={viewOnly}
                iconElement={<Icon name="Plus" />}
                onClick={triggerOpenSheet}
              >
                {t('contacts.addContact')}
              </Button>
            )
          }
        />
      )}
      <ContactsSheets isSheetOpen={isContactSheetOpen} />
    </Box>
  )
}
